import { get, patch, post } from '@/lib/api';
import { rec, str, strOrNull } from './normalize';
import type { Session, SessionTeam } from '@/types';

/**
 * ── AUTH INTEGRATION POINT ──────────────────────────────────────────────────
 *
 * v1 obtained the session server-side (`await auth()` in every RSC + route) and
 * shipped `isAdmin` / `activeTeamId` down as props. A SPA needs that as one
 * endpoint. `apps/server` is moving to Better Auth with Google OAuth, so the
 * contract this file expects is:
 *
 *   GET  /api/session           -> 200 SessionPayload | 401
 *   POST /api/auth/sign-out     -> 204
 *   GET  /api/auth/sign-in/google?redirect=<url>   (full page navigation)
 *
 * where SessionPayload is:
 *
 *   {
 *     user:  { id, name, email, image, timezone },
 *     team:  { id, name, billingType } | null,   // the ACTIVE team
 *     role:  'admin' | 'member',                 // role in the active team
 *     teams: [{ id, name, billingType }]         // all teams the user is in
 *   }
 *
 * Team scoping stays implicit — the client never sends a teamId, it only
 * switches the active team via `setActiveTeam`. Better Auth's own session
 * endpoint (`/api/auth/get-session`) does not carry team/role, so the server
 * should expose the enriched shape above rather than making the SPA stitch
 * three calls together on every cold start.
 */

const SESSION_PATH = '/api/session';

export /** Used when the server says nothing — never send to a real client on a guess. */
const SAFE_FALLBACK = 'rhettowen26@gmail.com';

/**
 * Only for the moment before the server has answered, or if it stops sending
 * the field. Mirrors RESEND_FROM_EMAIL in apps/server/src/lib/constants.ts —
 * the server's value always wins.
 */
const DEFAULT_FROM_EMAIL = 'invoices@billable.to';

function toSessionTeam(v: unknown): SessionTeam {
  const t = rec(v);
  const billingType = t.billingType === 'direct' ? 'direct' : 'case_based';
  const team: SessionTeam = { id: str(t.id), name: str(t.name, 'My team'), billingType };
  if (t.isDemo === true) {
    team.isDemo = true;
    team.demoKind =
      t.demoKind === 'lcp' || t.demoKind === 'legal' || t.demoKind === 'consulting'
        ? t.demoKind
        : null;
    team.demoState = t.demoState === 'full' || t.demoState === 'empty' ? t.demoState : null;
    team.demoContactEmail = strOrNull(t.demoContactEmail);
  }
  return team;
}

export async function fetchSession(signal?: AbortSignal): Promise<Session> {
  const raw = rec(await get<unknown>(SESSION_PATH, undefined, signal));
  const user = rec(raw.user);
  const role = raw.role === 'admin' ? 'admin' : 'member';
  const teams = Array.isArray(raw.teams) ? raw.teams.map(toSessionTeam) : [];

  return {
    user: {
      id: str(user.id),
      name: strOrNull(user.name),
      email: strOrNull(user.email),
      image: strOrNull(user.image),
      timezone: str(user.timezone, Intl.DateTimeFormat().resolvedOptions().timeZone)
    },
    team: raw.team ? toSessionTeam(raw.team) : (teams[0] ?? null),
    role,
    isAdmin: role === 'admin',
    // Missing means no onboarding: an invitee must never be walked through setup.
    isTeamCreator: raw.isTeamCreator === true,
    isPlatformAdmin: raw.isPlatformAdmin === true,
    isDemoAccount: raw.isDemoAccount === true,
    flags: Object.fromEntries(
      Object.entries(rec(raw.flags)).filter((entry): entry is [string, boolean] => entry[1] === true)
    ),
    teams,
    // Fail safe: only an explicit null from the server unlocks real
    // recipients. A missing or malformed field means redirect, because the
    // cost of guessing wrong is a test invoice in a client's inbox.
    emailRedirectTo:
      raw.emailRedirectTo === null ? null : strOrNull(raw.emailRedirectTo) ?? SAFE_FALLBACK,
    emailFrom: str(raw.emailFrom, DEFAULT_FROM_EMAIL)
  };
}

export async function setActiveTeam(teamId: string) {
  await patch('/api/users/active-team', { teamId });
}

export async function signOut() {
  // The empty object is load-bearing. `post()` only sets a Content-Type when
  // there is a body, and Better Auth's router answers a POST without
  // `application/json` with a 415 before the handler ever runs -- so a
  // body-less sign-out left the session cookie in place.
  await post('/api/auth/sign-out', {});
}

/** Full-page redirect — OAuth cannot happen in an XHR. */
export function signInWithGoogle(redirectTo = window.location.pathname) {
  const url = new URL('/api/auth/sign-in/google', window.location.origin);
  url.searchParams.set('redirect', redirectTo);
  window.location.href = url.toString();
}

/** Email a six-digit sign-in code. Works for any address, Google or not. */
export async function sendSignInCode(email: string) {
  await post('/api/auth/email-otp/send-verification-otp', { email, type: 'sign-in' });
}

/** Trade the emailed code for a session cookie. */
export async function verifySignInCode(email: string, otp: string) {
  await post('/api/auth/sign-in/email-otp', { email, otp });
}

/** The sign-in screen, which offers Google and an emailed code. */
export function signInPath(redirectTo = window.location.pathname, email?: string) {
  const params = new URLSearchParams({ redirect: redirectTo });
  if (email) params.set('email', email);
  return `/sign-in?${params}`;
}
